import { api, acct } from "./client";
import type { AccountSummary, Envelope, ListQuery, Page } from "./types";

/**
 * Accounts (cb_accounts), services (cb_services) and the balance
 * (cb_ledgers). Facts in doc/engineering/switchboard/phase-6-accounts-platform.md
 * section 3. Two things every screen must know: a single GET puts
 * `enabled`, `is_reseller`, `reseller_id`, `superduper_admin` and
 * `billing_mode` in the envelope's `metadata`, never in `data`; and no
 * listing row carries any of them.
 */

export interface AccountDoc {
  id: string;
  name: string;
  realm?: string;
  timezone?: string;
  language?: string;
  /** Never present on a GET (it lives in `metadata.enabled`); written with PATCH from the parent. */
  enabled?: boolean;
  is_reseller?: boolean;
  reseller_id?: string;
  org?: string;
  flags?: string[];
  /** Server-written, recomputed asynchronously after every create, delete and move; counts every depth. */
  descendants_count?: number;
  caller_id?: Record<string, { name?: string; number?: string }>;
  [key: string]: unknown;
}

/** The envelope `metadata` of `GET /v2/accounts/{id}`. */
export interface AccountMeta {
  id?: string;
  created?: number;
  modified?: number;
  enabled?: boolean;
  /** From the services database, not the account document. */
  is_reseller?: boolean;
  reseller_id?: string;
  superduper_admin?: boolean;
  billing_mode?: "limits_only" | "normal" | "manual" | string;
  wnm_allow_additions?: boolean;
  api_key?: string;
}

export interface AccountDetail extends AccountDoc {
  meta: AccountMeta;
}

/** Row of `children`/`siblings`: has the count, not the tree. */
export interface AccountChildRow {
  id: string;
  name: string;
  realm?: string;
  descendants_count?: number;
  flags?: string[];
}
/** Row of `descendants`: has the tree (ancestor ids), not the count. */
export interface AccountDescendantRow {
  id: string;
  name: string;
  realm?: string;
  tree?: string[];
  flags?: string[];
}
/** Row of `tree`/`parents`: the path from the operator's account down to (excluding) the target. */
export interface AccountTreeRow {
  id: string;
  name: string;
  realm?: string;
  flags?: string[];
}

type AccountEnvelope = Envelope<AccountDoc> & { metadata?: AccountMeta };

export const accounts = {
  get: (id: string) => api.get<AccountDoc>(acct(id)),
  /** The document plus the envelope's read-only facts. */
  detail: async (id: string): Promise<AccountDetail> => {
    const env = await api.get<AccountEnvelope>(acct(id), { envelope: true });
    return { ...env.data, meta: env.metadata ?? {} };
  },
  /** Paginated (50); pass `paginate: false` for the whole set of children. */
  children: (id: string, q?: ListQuery): Promise<Page<AccountChildRow>> => api.list<AccountChildRow>(acct(id, "children"), q),
  descendants: (id: string, q?: ListQuery): Promise<Page<AccountDescendantRow>> => api.list<AccountDescendantRow>(acct(id, "descendants"), q),
  /** Siblings of `id` (children of its parent, including `id`); 403 on the operator's own account unless superduper. */
  siblings: (id: string, q?: ListQuery): Promise<Page<AccountChildRow>> => api.list<AccountChildRow>(acct(id, "siblings"), q),
  /** The path from the operator's account to the target; `[]` for the operator's own account. */
  parents: (id: string) => api.get<AccountTreeRow[]>(acct(id, "parents")),
  /** Prefix search on name across every account the token can see. */
  search: (prefix: string) => api.get<AccountSummary[]>("/search", { query: { t: "account", q: "name", v: prefix } }),
  /**
   * Create a child of `parentId`. No 402 here (the admin user, limits and
   * numbers bill separately). `send_email_on_creation` defaults to true on
   * the server.
   */
  create: (parentId: string, doc: Partial<AccountDoc> & { name: string }, opts: { sendEmail?: boolean } = {}) =>
    api.put<AccountDoc>(acct(parentId), doc, { query: opts.sendEmail === false ? { send_email_on_creation: "false" } : undefined }),
  update: (id: string, patch: Partial<AccountDoc>) => api.patch<AccountDoc>(acct(id), patch),
  /** Only an ancestor can do this: the key is dropped when an account patches itself. Does not cascade. */
  setEnabled: (id: string, enabled: boolean) => api.patch<AccountDoc>(acct(id), { enabled }),
  /** `POST .../move {to}`; who may is `crossbar.accounts.allow_move` (default superduper); a destination inside the subtree is 403. */
  move: (id: string, to: string) => api.post<AccountDoc>(acct(id, "move"), { to }),
  /** Superduper only. A descendant that is already a reseller answers 500 `account_has_descendants`. */
  promote: (id: string) => api.put<AccountDoc>(acct(id, "reseller"), {}),
  demote: (id: string) => api.delete<AccountDoc>(acct(id, "reseller")),
  /**
   * Hard delete: the account database, every MODB, the SIP credentials,
   * and every phone number is released. Refused for the operator's own
   * account (403), the master (405), an account with descendants (500
   * `account_has_descendants`) or an active port (500).
   */
  remove: (id: string) => api.delete<AccountDoc>(acct(id)),
};

/* ---- documents the Vonix portal owns ---- */

/**
 * The portal owns the users of the companies it manages, and one device
 * per user per platform; the tie line stamps everything it maintains with
 * `ui_metadata.origin: portal`. The marker is the test, not the account:
 * a portal-managed account can still hold users and devices this console
 * created, and those stay editable.
 */
export const PORTAL_MARKER = "portal";

/** Any document that may carry the stamp. */
export interface PortalMarked {
  ui_metadata?: { origin?: string };
}

export const isPortalManagedDoc = (doc?: PortalMarked | null): boolean => doc?.ui_metadata?.origin === PORTAL_MARKER;

/**
 * The ids of an account's marked users (or devices). `crossbar_listing`
 * emits a fixed set of keys and `ui_metadata` is not among them, so the
 * marker cannot be read off a listing row. A `filter_` query is the way
 * in: crossbar answers one with `include_docs` and applies the filter to
 * the whole document (`crossbar_view:maybe_set_include_docs/2`,
 * `crossbar_filter:filter_doc_by_querystring/2`), and the rows that come
 * back are the marked ones.
 */
export async function portalManagedIds(accountId: string, noun: "users" | "devices"): Promise<Set<string>> {
  const page = await api.list<{ id: string }>(acct(accountId, noun), { paginate: false, filters: { "ui_metadata.origin": PORTAL_MARKER } });
  return new Set(page.items.map((row) => row.id));
}

/** An account is portal-managed when any of its users carries the marker. */
export async function isPortalManaged(accountId: string): Promise<boolean> {
  const page = await api.list<{ id: string }>(acct(accountId, "users"), { pageSize: 1, filters: { "ui_metadata.origin": PORTAL_MARKER } });
  return page.items.length > 0;
}

/* ---- services and plans (cb_services); no /v2/service_plans in this fork ---- */

export interface ServicePlanRow {
  id: string;
  name?: string;
  description?: string;
  category?: string;
}
export interface AssignedPlan {
  vendor_id?: string;
  overrides?: Record<string, unknown>;
  contract?: Record<string, unknown>;
  [key: string]: unknown;
}
export interface ServicesSummary {
  plans?: Record<string, AssignedPlan>;
  overrides?: Record<string, unknown>;
  invoices?: unknown[];
  /** `account` (this account) and `cascade` (with descendants), each `{<category>: {<item>: quantity}}`. */
  quantities?: { account?: Record<string, Record<string, number>>; cascade?: Record<string, Record<string, number>> };
  reseller?: { id?: string; is_reseller?: boolean };
  ratedeck?: Record<string, unknown>;
  limits?: Record<string, unknown>;
  [key: string]: unknown;
}

export const services = {
  /** Plans the account's reseller offers: `{id, name, description, category}`. */
  available: (accountId: string, details = false) =>
    api.get<ServicePlanRow[]>(acct(accountId, "services", "available"), { query: details ? { details: "true" } : undefined }).then((rows) => (Array.isArray(rows) ? rows : [])),
  /** The assigned plans object (`plans`), keyed by plan id. */
  assigned: async (accountId: string): Promise<Record<string, AssignedPlan>> => {
    const res = await api.get<{ plans?: Record<string, AssignedPlan> } | Record<string, AssignedPlan>>(acct(accountId, "services"));
    const r = res as { plans?: Record<string, AssignedPlan> };
    return r?.plans ?? ((res as Record<string, AssignedPlan>) ?? {});
  },
  /** Needs the account's reseller or superduper (403). No 402. */
  assign: (accountId: string, planId: string, body: Partial<AssignedPlan> = {}) => api.post<ServicesSummary>(acct(accountId, "services", planId), body),
  unassign: (accountId: string, planId: string) => api.delete<ServicesSummary>(acct(accountId, "services", planId)),
  bulk: (accountId: string, body: { add?: string[]; delete?: string[]; overrides?: Record<string, unknown>; merge?: boolean }) =>
    api.post<ServicesSummary>(acct(accountId, "services"), body),
  summary: (accountId: string) => api.get<ServicesSummary>(acct(accountId, "services", "summary")),
  /** Prices a change without committing it. */
  quote: (accountId: string, body: Record<string, unknown>) => api.post<ServicesSummary>(acct(accountId, "services", "quote"), body),
};

export const ledgers = {
  /** The account balance in dollars. */
  total: (accountId: string) => api.get<{ amount?: number }>(acct(accountId, "ledgers", "total")).then((r) => (typeof r?.amount === "number" ? r.amount : 0)),
};

/* ---- Account settings: the fields the settings page edits (accounts.json plus what monster-ui wrote) ---- */

export interface CallerIdEntry {
  name?: string;
  number?: string;
}
export interface AccountSettingsDoc extends AccountDoc {
  caller_id?: { internal?: CallerIdEntry; external?: CallerIdEntry; emergency?: CallerIdEntry; asserted?: CallerIdEntry & { realm?: string } };
  music_on_hold?: { media_id?: string; options?: string[] };
  contact?: { technical?: { name?: string; email?: string; number?: string }; billing?: Record<string, string | undefined> };
  contract?: { representative?: { name?: string; user_id?: string; account_id?: string }; end_date?: number };
  custom_notes?: string;
  announcement?: string;
  call_restriction?: Record<string, { action?: "inherit" | "deny" }>;
  outbound_flags?: { static?: string[]; dynamic?: string[] };
  preflow?: { always?: string };
}

/**
 * PATCH with nested objects: crossbar deep-merges, and a `null` leaf removes
 * the key. Typed loosely so sections can send `{caller_id: {external: null}}`.
 */
export type AccountPatch = { [K in keyof AccountSettingsDoc]?: AccountSettingsDoc[K] | null | Record<string, unknown> };
export const accountSettings = {
  get: (id: string) => api.get<AccountSettingsDoc>(acct(id)),
  patch: (id: string, patch: AccountPatch) => api.patch<AccountSettingsDoc>(acct(id), patch),
};
